<template>
  <div>
    <h2 class="mb-3">
      Flagged Calls
    </h2>

    <!-- Statistics -->
    <CCard
      v-if="statistics"
      class="mb-3 shadow-sm"
    >
      <CCardHeader
        :class="['bg-white d-flex align-items-center', { 'border-bottom-0': !statisticsExpanded }]"
        style="cursor: pointer;"
        @click="statisticsExpanded = !statisticsExpanded"
      >
        <span>
          <strong>Statistics</strong>
          <CIcon
            :name="statisticsExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
            :class="['ml-2', statisticsExpanded ? 'text-muted' : 'text-success']"
            size="sm"
          />
        </span>
      </CCardHeader>
      <CCollapse :show="statisticsExpanded">
        <CCardBody>
          <!-- Timeline Chart -->
          <div
            v-if="chartPrimaries.length > 0"
            class="mb-2 d-flex align-items-center flex-wrap"
          >
            <small class="text-muted mr-2">Showing:</small>
            <span
              v-for="(val, idx) in chartPrimaries"
              :key="'p-' + val"
              style="cursor: pointer;"
              @click="removePrimary(idx)"
            >
              <CBadge
                class="mr-1 mb-1 chart-filter-badge"
                :style="{ backgroundColor: primaryColor(val), color: '#fff' }"
                :title="chartPrimaryType === 'tag' ? tagDescription(val) : ''"
              >
                {{ chartPrimaryType === 'tag' ? formatTag(val) : val }}
                <CIcon
                  name="cil-x"
                  size="sm"
                  class="ml-1"
                />
              </CBadge>
            </span>
            <template v-if="chartCrossFilters.length > 0">
              <small class="text-muted mx-2">filtered by:</small>
              <span
                v-for="cf in chartCrossFilters"
                :key="'cf-' + cf.type + '-' + cf.value"
                style="cursor: pointer;"
                @click="removeCrossFilter(cf.type, cf.value)"
              >
                <CBadge
                  color="secondary"
                  class="mr-1 mb-1 chart-filter-badge"
                  :title="cf.type === 'tag' ? tagDescription(cf.value) : ''"
                >
                  {{ cf.type === 'tag' ? formatTag(cf.value) : cf.value }}
                  <CIcon
                    name="cil-x"
                    size="sm"
                    class="ml-1"
                  />
                </CBadge>
              </span>
            </template>
            <span
              class="ml-2"
              style="cursor: pointer;"
              @click="clearChartSelections"
            >
              <CBadge
                color="light"
                class="mr-1 mb-1 text-dark border"
              >
                Clear all
                <CIcon
                  name="cil-x"
                  size="sm"
                  class="ml-1"
                />
              </CBadge>
            </span>
          </div>
          <div
            v-if="timelineChartData.labels.length > 0"
            style="width: 100%; position: relative;"
          >
            <CChartLine
              :datasets="timelineChartData.datasets"
              :labels="timelineChartData.labels"
              :options="timelineChartOptions"
              :plugins="timelineChartPlugins"
              style="height: 250px; width: 100%;"
            />
          </div>
          <div
            v-else
            class="text-center text-muted py-4"
          >
            No data available
          </div>

          <!-- Charts Row -->
          <div class="row">
            <!-- Top Sellers by Fraud -->
            <div class="col-md-4">
              <CCard class="h-100 mb-3 shadow-sm">
                <CCardHeader class="bg-white">
                  <strong>Top Sellers</strong>
                </CCardHeader>
                <CCardBody>
                  <div
                    v-if="computedStatistics && computedStatistics.topSellersByFraudCount.length > 0"
                  >
                    <div
                      v-for="(seller, index) in computedStatistics.topSellersByFraudCount.slice(0, 15)"
                      :key="index"
                      :class="['d-flex justify-content-between align-items-center mb-2 stat-item', { 'stat-item--active': isChartFilterActive('seller', seller.sellerName) }]"
                      :style="chartFilterStyle('seller', seller.sellerName)"
                      @click="toggleChartFilter('seller', seller.sellerName)"
                    >
                      <span class="text-truncate mr-2">{{ seller.sellerName }}</span>
                      <div class="d-flex align-items-center text-nowrap">
                        <CBadge class="mr-1 badge-fraud-count">
                          {{ seller.fraudCount }}
                        </CBadge>
                        <CBadge class="badge-suspicious-count">
                          {{ seller.suspiciousCount }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center text-muted p-4"
                  >
                    No data
                  </div>
                </CCardBody>
              </CCard>
            </div>

            <!-- Top Categories -->
            <div class="col-md-4">
              <CCard class="h-100 mb-3 shadow-sm">
                <CCardHeader class="bg-white">
                  <strong>Top Categories</strong>
                </CCardHeader>
                <CCardBody>
                  <div
                    v-if="computedStatistics && computedStatistics.topCategories.length > 0"
                  >
                    <div
                      v-for="(cat, index) in computedStatistics.topCategories.slice(0, 15)"
                      :key="index"
                      :class="['d-flex justify-content-between align-items-center mb-2 stat-item', { 'stat-item--active': isChartFilterActive('category', cat.categoryName) }]"
                      :style="chartFilterStyle('category', cat.categoryName)"
                      @click="toggleChartFilter('category', cat.categoryName)"
                    >
                      <span class="text-truncate mr-2">{{ cat.categoryName }}</span>
                      <div class="d-flex align-items-center text-nowrap">
                        <CBadge class="mr-1 badge-fraud-count">
                          {{ cat.fraudCount }}
                        </CBadge>
                        <CBadge class="badge-suspicious-count">
                          {{ cat.suspiciousCount }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center text-muted p-4"
                  >
                    No data
                  </div>
                </CCardBody>
              </CCard>
            </div>

            <!-- Filter Tag Breakdown -->
            <div class="col-md-4">
              <CCard class="h-100 mb-3 shadow-sm">
                <CCardHeader class="bg-white">
                  <strong>By Filter Tag</strong>
                </CCardHeader>
                <CCardBody>
                  <div
                    v-if="computedStatistics && computedStatistics.byFilterTag.length > 0"
                  >
                    <div
                      v-for="(tag, index) in computedStatistics.byFilterTag.slice(0, 15)"
                      :key="index"
                      :class="['d-flex justify-content-between align-items-center mb-2 stat-item', { 'stat-item--active': isChartFilterActive('tag', tag.tag) }]"
                      :style="chartFilterStyle('tag', tag.tag)"
                      :title="tagDescription(tag.tag)"
                      @click="toggleChartFilter('tag', tag.tag)"
                    >
                      <span class="text-truncate mr-2">{{ formatTag(tag.tag) }}</span>
                      <div class="d-flex align-items-center text-nowrap">
                        <CBadge class="mr-1 badge-fraud-count">
                          {{ tag.fraudCount }}
                        </CBadge>
                        <CBadge class="badge-suspicious-count">
                          {{ tag.suspiciousCount }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center text-muted p-4"
                  >
                    No data
                  </div>
                </CCardBody>
              </CCard>
            </div>
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <!-- Filters -->
    <CCard class="mb-3">
      <CCardBody>
        <div class="row mb-0 filters-row">
          <div class="col">
            <label class="small font-weight-bold">Call ID / Caller</label>
            <input
              v-model="filterCallId"
              type="text"
              class="form-control form-control-sm"
              placeholder="Search by Call ID or Caller..."
            >
          </div>
          <div class="col">
            <label class="small font-weight-bold">Seller</label>
            <select
              v-model="filterSeller"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in sellerOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Buyer</label>
            <select
              v-model="filterBuyer"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in buyerOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Category</label>
            <select
              v-model="filterCategory"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in categoryOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Filter Tags</label>
            <div
              v-click-outside="() => { tagDropdownOpen = false }"
              class="position-relative"
            >
              <button
                type="button"
                class="form-control form-control-sm d-flex align-items-center justify-content-between text-left"
                @click="tagDropdownOpen = !tagDropdownOpen"
              >
                <span class="text-truncate">{{ filterTagsLabel }}</span>
                <CIcon
                  name="cil-chevron-bottom"
                  size="sm"
                />
              </button>
              <div
                v-show="tagDropdownOpen"
                class="position-absolute bg-white border rounded shadow-sm px-3 py-2 mt-1"
                style="max-height: 250px; overflow-y: auto; width: 100%; z-index: 1050;"
              >
                <div class="d-flex mb-2">
                  <a
                    href="#"
                    class="small mr-3"
                    @click.prevent="filterTags = [...tagOptions]"
                  >Select All</a>
                  <a
                    href="#"
                    class="small"
                    @click.prevent="filterTags = []"
                  >Clear All</a>
                </div>
                <div
                  v-for="tag in tagOptions"
                  :key="tag"
                  class="form-check mb-1"
                >
                  <input
                    :id="'tag-' + tag"
                    v-model="filterTags"
                    type="checkbox"
                    class="form-check-input"
                    :value="tag"
                  >
                  <label
                    :for="'tag-' + tag"
                    class="form-check-label small"
                    :title="tagDescription(tag)"
                  >{{ formatTag(tag) }}</label>
                </div>
              </div>
            </div>
          </div>
          <div class="col d-flex align-items-end justify-content-end">
            <button
              type="button"
              class="btn btn-sm btn-outline-dark mr-2"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              <CIcon
                name="cil-x"
                size="sm"
                class="mr-1"
              />
              Clear
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="loading"
              @click="handleFetch({ force: true })"
            >
              <CSpinner
                v-if="loading"
                size="sm"
                class="mr-1"
              />
              <CIcon
                v-else
                name="cil-sync"
                size="sm"
                class="mr-1"
              />
              Refresh
            </button>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Frauds Table -->
    <CCard class="mb-3">
      <CCardHeader
        :class="['bg-white d-flex align-items-center', { 'border-bottom-0': !fraudsExpanded }]"
        style="cursor: pointer;"
        @click="fraudsExpanded = !fraudsExpanded"
      >
        <strong>Frauds</strong>
        <CBadge
          class="ml-2 badge-fraud-count"
        >
          {{ fraudResults.length }}
        </CBadge>
        <CIcon
          :name="fraudsExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
          :class="['ml-2', fraudsExpanded ? 'text-muted' : 'text-danger']"
          size="sm"
        />
        <label
          v-if="fraudsExpanded"
          class="pending-toggle ml-auto mb-0"
          @click.stop
        >
          <small><strong>Show Pending Resolution Only</strong></small>
          <CSwitch
            variant="3d"
            color="primary"
            size="sm"
            :checked.sync="fraudsPendingOnly"
            class="ml-1"
          />
        </label>
      </CCardHeader>
      <CCollapse :show="fraudsExpanded">
        <CCardBody>
          <FlaggedCallsTable
            :items="fraudResults"
            :loading="loading"
            :slack-link="getSlackLink"
            @status-update="handleStatusUpdate"
          />
        </CCardBody>
      </CCollapse>
    </CCard>

    <!-- Suspicious Calls Table -->
    <CCard>
      <CCardHeader
        :class="['bg-white d-flex align-items-center', { 'border-bottom-0': !suspiciousExpanded }]"
        style="cursor: pointer;"
        @click="suspiciousExpanded = !suspiciousExpanded"
      >
        <strong>Suspicious calls</strong>
        <CBadge
          class="ml-2 badge-suspicious-count"
        >
          {{ suspiciousResults.length }}
        </CBadge>
        <CIcon
          :name="suspiciousExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
          :class="['ml-2', suspiciousExpanded ? 'text-muted' : 'text-warning']"
          size="sm"
        />
        <label
          v-if="suspiciousExpanded"
          class="pending-toggle ml-auto mb-0"
          @click.stop
        >
          <small><strong>Show Pending Resolution Only</strong></small>
          <CSwitch
            variant="3d"
            color="primary"
            size="sm"
            :checked.sync="suspiciousPendingOnly"
            class="ml-1"
          />
        </label>
      </CCardHeader>
      <CCollapse :show="suspiciousExpanded">
        <CCardBody>
          <FlaggedCallsTable
            :items="suspiciousResults"
            :loading="loading"
            :slack-link="getSlackLink"
            @status-update="handleStatusUpdate"
          />
        </CCardBody>
      </CCollapse>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { CChartLine } from '@coreui/vue-chartjs'
import { showErrorMessage } from '@/notification-utils'
import FILTER_TAG_DESCRIPTIONS from './filterTagDescriptions'
import FlaggedCallsTable from './FlaggedCallsTable.vue'

export default {
  name: 'Frauds',

  title: 'Flagged Calls',

  components: {
    CChartLine,
    FlaggedCallsTable
  },

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }

        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },

  data() {
    return {
      loading: false,
      tagDropdownOpen: false,
      statisticsExpanded: false,
      fraudsExpanded: true,
      fraudsPendingOnly: true,
      suspiciousExpanded: true,
      suspiciousPendingOnly: true,
      chartPrimaryType: null,
      chartPrimaries: [],
      chartCrossFilters: [],
      chartColorMap: {},
      chartNextColor: 0,
      chartColors: [
        '#321fdb', '#e55353', '#2eb85c', '#f9b115', '#17a2b8',
        '#6f42c1', '#20c997', '#fd7e14', '#6610f2', '#e83e8c'
      ],
      filterSeller: '',
      filterBuyer: '',
      filterCategory: '',
      filterCallId: '',
      filterTags: [],
    }
  },

  computed: {
    ...mapGetters('frauds', ['results', 'statistics', 'slackTeamId', 'slackFraudChannelId', 'slackSuspiciousChannelId']),

    chartFilteredResults() {
      let items = this.results

      if (this.chartPrimaries.length > 0) {
        items = items.filter(r => {
          if (this.chartPrimaryType === 'seller') {
            return this.chartPrimaries.includes(r.sellerName)
          }

          if (this.chartPrimaryType === 'category') {
            return this.chartPrimaries.includes(r.categoryName)
          }

          return this.chartPrimaries.some(v => this.splitTags(r.filterTags).includes(v))
        })
      }

      const groups = {}
      for (const cf of this.chartCrossFilters) {
        if (!groups[cf.type]) {
          groups[cf.type] = []
        }

        groups[cf.type].push(cf.value)
      }

      for (const [type, values] of Object.entries(groups)) {
        if (type === 'seller') {
          items = items.filter(r => values.includes(r.sellerName))
        } else if (type === 'category') {
          items = items.filter(r => values.includes(r.categoryName))
        } else if (type === 'tag') {
          items = items.filter(r => values.some(v => this.splitTags(r.filterTags).includes(v)))
        }
      }

      return items
    },

    crossFilteredResults() {
      let items = this.results

      const groups = {}
      for (const cf of this.chartCrossFilters) {
        if (!groups[cf.type]) {
          groups[cf.type] = []
        }

        groups[cf.type].push(cf.value)
      }

      for (const [type, values] of Object.entries(groups)) {
        if (type === 'seller') {
          items = items.filter(r => values.includes(r.sellerName))
        } else if (type === 'category') {
          items = items.filter(r => values.includes(r.categoryName))
        } else if (type === 'tag') {
          items = items.filter(r => values.some(v => this.splitTags(r.filterTags).includes(v)))
        }
      }

      return items
    },

    computedStatistics() {
      const fullResults = this.chartFilteredResults
      const primaryResults = this.crossFilteredResults

      const isFraud = (fc) => fc === 'Fraud' || fc === 0
      const isSuspicious = (fc) => fc === 'Suspicious' || fc === 1

      const buildGroup = (results, keyFn) => {
        const map = {}
        for (const r of results) {
          const key = keyFn(r)
          if (!key) {
            continue
          }

          const keys = Array.isArray(key) ? key : [key]
          for (const k of keys) {
            if (!map[k]) {
              map[k] = { fraudCount: 0, suspiciousCount: 0 }
            }

            if (isFraud(r.flagCategory)) {
              map[k].fraudCount++
            }

            if (isSuspicious(r.flagCategory)) {
              map[k].suspiciousCount++
            }
          }
        }

        return map
      }

      const sellerSrc = this.chartPrimaryType === 'seller' ? primaryResults : fullResults
      const sellerMap = buildGroup(sellerSrc, r => r.sellerName)
      const topSellersByFraudCount = Object.entries(sellerMap)
        .map(([name, counts]) => ({ sellerName: name, ...counts }))
        .sort((a, b) => (b.fraudCount + b.suspiciousCount) - (a.fraudCount + a.suspiciousCount))

      const categorySrc = this.chartPrimaryType === 'category' ? primaryResults : fullResults
      const categoryMap = buildGroup(categorySrc, r => r.categoryName)
      const topCategories = Object.entries(categoryMap)
        .map(([name, counts]) => ({ categoryName: name, ...counts }))
        .sort((a, b) => (b.fraudCount + b.suspiciousCount) - (a.fraudCount + a.suspiciousCount))

      const tagSrc = this.chartPrimaryType === 'tag' ? primaryResults : fullResults
      const tagMap = buildGroup(tagSrc, r => {
        if (!r.filterTags) {
          return null
        }

        return this.splitTags(r.filterTags)
      })
      const byFilterTag = Object.entries(tagMap)
        .map(([tag, counts]) => ({ tag, ...counts }))
        .sort((a, b) => (b.fraudCount + b.suspiciousCount) - (a.fraudCount + a.suspiciousCount))

      if (topSellersByFraudCount.length === 0 && topCategories.length === 0 && byFilterTag.length === 0) {
        return null
      }

      return { topSellersByFraudCount, topCategories, byFilterTag }
    },

    sellerOptions() {
      return [...new Set(this.results.map(r => r.sellerName).filter(Boolean))].sort()
    },

    buyerOptions() {
      return [...new Set(this.results.map(r => r.buyerName).filter(Boolean))].sort()
    },

    categoryOptions() {
      return [...new Set(this.results.map(r => r.categoryName).filter(Boolean))].sort()
    },

    tagOptions() {
      const tags = new Set()
      for (const r of this.results) {
        if (!r.filterTags) {
          continue
        }

        for (const t of r.filterTags.split(',').map(s => s.trim()).filter(Boolean)) {
          tags.add(t)
        }
      }

      return [...tags].sort()
    },

    filterTagsLabel() {
      if (this.filterTags.length === 0) {
        return 'None'
      }

      if (this.filterTags.length === this.tagOptions.length) {
        return 'All'
      }

      if (this.filterTags.length === 1) {
        return this.formatTag(this.filterTags[0])
      }

      return this.filterTags.length + ' selected'
    },

    hasActiveFilters() {
      return this.filterCallId !== ''
        || this.filterSeller !== ''
        || this.filterBuyer !== ''
        || this.filterCategory !== ''
        || this.filterTags.length !== this.tagOptions.length
    },

    timelineChartData() {
      if (this.results.length === 0) {
        return { labels: [], datasets: [] }
      }

      const earliest = moment().subtract(29, 'days').format('YYYY-MM-DD')
      const days = []
      const cursor = moment(earliest).startOf('day')
      const today = moment().startOf('day')
      while (cursor.isSameOrBefore(today)) {
        days.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
      }

      const labels = days.map(d => moment(d).format('MMM DD'))

      const bucketize = (items) => {
        const buckets = {}
        for (const r of items) {
          const day = moment(r.receivedAt).format('YYYY-MM-DD')
          if (!buckets[day]) {
            buckets[day] = { fraud: 0, suspicious: 0 }
          }

          if (r.flagCategory === 'Fraud' || r.flagCategory === 0) {
            buckets[day].fraud++
          }

          if (r.flagCategory === 'Suspicious' || r.flagCategory === 1) {
            buckets[day].suspicious++
          }
        }

        return buckets
      }

      const matchesPrimary = (r, value) => {
        if (this.chartPrimaryType === 'seller') {
          return r.sellerName === value
        }

        if (this.chartPrimaryType === 'category') {
          return r.categoryName === value
        }

        return this.splitTags(r.filterTags).includes(value)
      }

      const applyCrossFilters = (items) => {
        const groups = {}
        for (const cf of this.chartCrossFilters) {
          if (!groups[cf.type]) {
            groups[cf.type] = []
          }

          groups[cf.type].push(cf.value)
        }

        for (const [type, values] of Object.entries(groups)) {
          if (type === 'seller') {
            items = items.filter(r => values.includes(r.sellerName))
          } else if (type === 'category') {
            items = items.filter(r => values.includes(r.categoryName))
          } else if (type === 'tag') {
            items = items.filter(r => values.some(v => this.splitTags(r.filterTags).includes(v)))
          }
        }

        return items
      }

      const makePair = (label, color, buckets) => {
        return [
          {
            label: label + ' — Fraud',
            backgroundColor: color,
            borderColor: color,
            fill: true,
            _gradientFill: true,
            data: days.map(d => buckets[d] ? buckets[d].fraud : 0),
            borderWidth: 1.5,
            borderDash: []
          },
          {
            label: label + ' — Suspicious',
            backgroundColor: 'transparent',
            borderColor: color + '80',
            fill: false,
            data: days.map(d => buckets[d] ? buckets[d].suspicious : 0),
            borderWidth: 1.5,
            borderDash: [5, 5]
          }
        ]
      }

      const datasets = []
      if (this.chartPrimaries.length === 0) {
        const buckets = bucketize(this.results)
        datasets.push(...makePair('All', '#000000', buckets))
      } else {
        for (let i = 0; i < this.chartPrimaries.length; i++) {
          const value = this.chartPrimaries[i]
          const label = this.chartPrimaryType === 'tag' ? this.formatTag(value) : value
          let items = this.results.filter(r => matchesPrimary(r, value))
          items = applyCrossFilters(items)
          const buckets = bucketize(items)
          datasets.push(...makePair(label, this.primaryColor(value), buckets))
        }
      }

      return { labels, datasets }
    },

    timelineChartOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            gridLines: {
              color: 'rgba(0, 0, 0, 0.04)'
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0
            },
            gridLines: {
              borderDash: [2, 4],
              color: '#ced4da'
            }
          }]
        },
        elements: {
          line: {},
          point: {
            radius: 0,
            hitRadius: 10,
            hoverRadius: 3
          }
        },
        tooltips: {
          enabled: false,
          mode: 'index',
          intersect: false,
          custom(tooltipModel) {
            let el = document.getElementById('chart-tooltip')
            if (!el) {
              el = document.createElement('div')
              el.id = 'chart-tooltip'
              document.body.appendChild(el)
            }

            if (tooltipModel.opacity === 0) {
              el.style.opacity = 0
              return
            }

            const dataPoints = tooltipModel.dataPoints || []
            if (dataPoints.length === 0) {
              el.style.opacity = 0
              return
            }

            const pointIndex = dataPoints[0].index
            let html = '<div style="font-size:12px;line-height:1.6;">'
            html += '<div style="font-weight:600;margin-bottom:2px;">' + tooltipModel.title[0] + '</div>'

            const datasets = this._chart.config.data.datasets
            for (let i = 0; i < datasets.length; i += 2) {
              const fraudDs = datasets[i]
              const suspDs = datasets[i + 1]
              const label = fraudDs.label.replace(' — Fraud', '')
              const fraudVal = fraudDs.data[pointIndex]
              const suspVal = suspDs ? suspDs.data[pointIndex] : 0
              const color = fraudDs.borderColor
              html += '<div style="display:flex;align-items:center;gap:6px;">'
              html += '<span style="width:8px;height:8px;border-radius:50%;background:' + color + ';display:inline-block;"></span>'
              html += '<span>' + label + '</span>'
              html += '<span style="background:rgba(229,83,83,0.15);color:#d93737;border-radius:3px;padding:0 4px;font-size:11px;">' + fraudVal + '</span>'
              html += '<span style="background:rgba(249,177,21,0.2);color:#c48b00;border-radius:3px;padding:0 4px;font-size:11px;">' + suspVal + '</span>'
              html += '</div>'
            }

            html += '</div>'
            el.innerHTML = html
            el.style.cssText = 'position:absolute;background:#fff;border:1px solid #e0e0e0;border-radius:6px;padding:8px 12px;pointer-events:none;box-shadow:0 2px 8px rgba(0,0,0,0.12);opacity:1;z-index:9999;transition:opacity 0.1s;'

            const pos = this._chart.canvas.getBoundingClientRect()
            el.style.left = pos.left + window.pageXOffset + tooltipModel.caretX + 'px'
            el.style.top = pos.top + window.pageYOffset + tooltipModel.caretY + 'px'
            el.style.transform = 'translate(-50%, -110%)'
          }
        }
      }
    },

    timelineChartPlugins() {
      return [{
        beforeDatasetsUpdate(chart) {
          const ctx = chart.ctx
          const chartArea = chart.chartArea
          if (!chartArea) {
            return
          }

          for (const ds of chart.config.data.datasets) {
            if (!ds._gradientFill) {
              continue
            }

            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
            gradient.addColorStop(0, ds.borderColor + '2E')
            gradient.addColorStop(1, ds.borderColor + '00')
            ds.backgroundColor = gradient
          }
        }
      }]
    },

    fraudResults() {
      let items = this.filteredResults.filter(r => r.flagCategory === 'Fraud' || r.flagCategory === 0)
      if (this.fraudsPendingOnly) {
        items = items.filter(r => r.status === 'New' || r.status === 0)
      }

      return items
    },

    suspiciousResults() {
      let items = this.filteredResults.filter(r => r.flagCategory === 'Suspicious' || r.flagCategory === 1)
      if (this.suspiciousPendingOnly) {
        items = items.filter(r => r.status === 'New' || r.status === 0)
      }

      return items
    },

    filteredResults() {
      let items = this.results

      if (this.filterCallId) {
        const search = this.filterCallId.toLowerCase()
        items = items.filter(r =>
          (r.callId && r.callId.toLowerCase().includes(search))
          || (r.callerId && r.callerId.toLowerCase().includes(search))
        )
      }

      if (this.filterSeller) {
        items = items.filter(r => r.sellerName === this.filterSeller)
      }

      if (this.filterBuyer) {
        items = items.filter(r => r.buyerName === this.filterBuyer)
      }

      if (this.filterCategory) {
        items = items.filter(r => r.categoryName === this.filterCategory)
      }

      items = items.filter(r => {
        const rowTags = this.splitTags(r.filterTags)
        return this.filterTags.some(t => rowTags.includes(t))
      })
      return items
    },
  },

  async created() {
    await this.handleFetch()
    this.filterTags = [...this.tagOptions]
  },

  methods: {
    ...mapActions('frauds', ['fetchResults', 'updateStatus']),

    async handleFetch({ force = false } = {}) {
      this.loading = true
      try {
        await this.fetchResults({ force })
      } catch (error) {
        showErrorMessage('Failed to fetch fraud data')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    assignColor(value) {
      if (!(value in this.chartColorMap)) {
        this.$set(this.chartColorMap, value, this.chartNextColor % this.chartColors.length)
        this.chartNextColor++
      }
    },

    primaryColor(value) {
      const idx = this.chartColorMap[value]
      return idx !== undefined ? this.chartColors[idx] : this.chartColors[0]
    },

    toggleChartFilter(type, value) {
      if (!this.chartPrimaryType) {
        this.chartPrimaryType = type
        this.assignColor(value)
        this.chartPrimaries.push(value)
        return
      }

      if (type === this.chartPrimaryType) {
        const idx = this.chartPrimaries.indexOf(value)
        if (idx !== -1) {
          this.chartPrimaries.splice(idx, 1)
          if (this.chartPrimaries.length === 0) {
            this.chartPrimaryType = null
            this.chartCrossFilters = []
            this.chartColorMap = {}
            this.chartNextColor = 0
          }
        } else if (this.chartPrimaries.length < 10) {
          this.assignColor(value)
          this.chartPrimaries.push(value)
        }
      } else {
        const idx = this.chartCrossFilters.findIndex(f => f.type === type && f.value === value)
        if (idx !== -1) {
          this.chartCrossFilters.splice(idx, 1)
        } else {
          this.chartCrossFilters.push({ type, value })
        }
      }
    },

    clearChartSelections() {
      this.chartPrimaries = []
      this.chartPrimaryType = null
      this.chartCrossFilters = []
      this.chartColorMap = {}
      this.chartNextColor = 0
    },

    removePrimary(idx) {
      this.chartPrimaries.splice(idx, 1)
      if (this.chartPrimaries.length === 0) {
        this.clearChartSelections()
      }
    },

    removeCrossFilter(type, value) {
      const idx = this.chartCrossFilters.findIndex(f => f.type === type && f.value === value)
      if (idx !== -1) {
        this.chartCrossFilters.splice(idx, 1)
      }
    },

    isChartFilterActive(type, value) {
      if (type === this.chartPrimaryType) {
        return this.chartPrimaries.includes(value)
      }

      return this.chartCrossFilters.some(f => f.type === type && f.value === value)
    },

    chartFilterStyle(type, value) {
      if (type === this.chartPrimaryType) {
        if (!this.chartPrimaries.includes(value)) {
          return {}
        }

        const color = this.primaryColor(value)
        return { backgroundColor: color + '20', borderLeft: '3px solid ' + color }
      }

      const isCross = this.chartCrossFilters.some(f => f.type === type && f.value === value)
      if (!isCross) {
        return {}
      }

      return { backgroundColor: '#6c757d20', borderLeft: '3px solid #6c757d' }
    },

    getSlackLink(flagCategory) {
      const isFraud = flagCategory === 'Fraud' || flagCategory === 0
      const channelId = isFraud ? this.slackFraudChannelId : this.slackSuspiciousChannelId
      return 'slack://channel?team=' + this.slackTeamId + '&id=' + channelId
    },

    clearFilters() {
      this.filterCallId = ''
      this.filterSeller = ''
      this.filterBuyer = ''
      this.filterCategory = ''
      this.filterTags = [...this.tagOptions]
    },

    formatTag(tag) {
      return tag.replace(/_/g, ' ')
    },

    tagDescription(tag) {
      return FILTER_TAG_DESCRIPTIONS[tag] || ''
    },

    splitTags(tags) {
      if (!tags) {
        return []
      }

      return tags.split(',').map(t => t.trim()).filter(t => t)
    },

    async handleStatusUpdate(item, status) {
      const newStatus = item.status === status ? 'New' : status
      try {
        await this.updateStatus({ id: item.callId, status: newStatus })
      } catch (error) {
        showErrorMessage('Failed to update status')
        console.error(error)
      }
    },
  },
}
</script>

<style scoped>
.filters-row {
  margin-left: -5px;
  margin-right: -5px;
}
.filters-row > [class*="col"] {
  padding-left: 5px;
  padding-right: 5px;
}
.stat-item {
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.15s;
}
.stat-item:hover {
  background-color: #f0f0f0;
}
.stat-item--active {
  padding-left: 6px;
}
.chart-filter-badge {
  padding-top: 0.35em;
}
.badge-fraud-count {
  background-color: rgba(229, 83, 83, 0.15);
  color: #d93737;
}
.badge-suspicious-count {
  background-color: rgba(249, 177, 21, 0.2);
  color: #c48b00;
}
.pending-toggle {
  cursor: default;
  display: flex;
  align-items: center;
}
.pending-toggle .c-switch {
  transform: scale(0.7);
  transform-origin: left center;
}
</style>
